import { ReactNode, useContext, createContext } from "react";
import styled, { css } from "styled-components";
import { theme } from "design-system/theme";
import { XcoreTheme } from "design-system/xcore/theme";
import * as system from "styled-system";
import TableHead from "design-system/xcore/Table/TableHead";
import TableBody from "design-system/xcore/Table/TableBody";
import { FlexProps, Flex, useTheme } from "@xcorejs/ui";

export type TableProps = {
  variant?: "normal" | "stripped";
  oddLineColor?: string;
  evenLineColor?: string;
  head?: ReactNode;
  body?: ReactNode;
  tableLayout?: "fixed" | "auto";
} & FlexProps &
  system.TypographyProps;

const TableStyle = styled(Flex)<TableProps>`
  width: 100%;
  flex-direction: column;

  ${(props) =>
    props.tableLayout === "fixed" &&
    css`
      table-layout: fixed;
    `}

  @media (min-width: ${theme.breakpoints[1]}) {
    display: table;
    border-spacing: 0;
  }

  th {
    display: block;

    @media (min-width: ${theme.breakpoints[1]}) {
      display: table-cell;
    }
  }

  thead {
    display: none;

    @media (min-width: ${theme.breakpoints[1]}) {
      text-align: left;
      display: table-header-group;
    }
  }

  tbody {
    tr {
      &:nth-child(odd) {
        background: ${(props) => props.oddLineColor};
      }

      &:nth-child(even) {
        background: ${(props) => props.evenLineColor};
      }
    }
  }
`;

const TableContext = createContext("normal");

export const useTableContext = () => {
  return useContext(TableContext);
};

const Table = ({ variant = "normal", children, head, body, tableLayout, ...props }: TableProps) => {
  const t = useTheme() as XcoreTheme;
  const tableProps = t.tables.default;
  const variantProps = t.tables.variant[variant];

  return (
    <TableContext.Provider value={variant}>
      <TableStyle as={"table" as any} {...tableProps} {...variantProps} tableLayout={tableLayout} {...props}>
        {head && <TableHead>{head}</TableHead>}

        {body ? <TableBody>{body}</TableBody> : children}
      </TableStyle>
    </TableContext.Provider>
  );
};

export default Table;
